import type { Location, NavigateFunction, NavigateOptions, To } from "react-router-dom";

/*
 * react-router v6 removed support for passing a custom history object to the
 * Router, but this app navigates from outside the React tree (redux actions in
 * AuthenticationActions / userActions). Instead of threading useNavigate()
 * through every caller, <NavigationBridge/> registers the router's navigate
 * function here and this module re-exposes the slice of the old v5 history API
 * the app actually used: push, replace and location.
 */
/**
 * Opaque payload react-router stores alongside a history entry and hands back
 * on `location.state`; the app does not read it.
 */
export type NavigationState = Record<string, string | number | boolean> | null;

let navigator: NavigateFunction | null = null;
let currentLocation: Location | null = null;

export function registerNavigator(
  navigate: NavigateFunction,
  location: Location
) {
  navigator = navigate;
  currentLocation = location;
}

function go(to: To, options?: NavigateOptions) {
  if (!navigator) {
    // Navigation attempted before the router mounted; nothing sensible to do.
    console.warn(`Navigation to "${String(to)}" ignored: router not mounted yet.`);
    return;
  }
  navigator(to, options);
}

const history = {
  push(to: To, state?: NavigationState) {
    go(to, { state });
  },
  replace(to: To, state?: NavigationState) {
    go(to, { state, replace: true });
  },
  get location(): Location | globalThis.Location {
    return currentLocation ?? window.location;
  }
};

export default history;
